import {
  RefObject,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useRouter } from "next/router";
import { useQueryClient } from "@tanstack/react-query";
import styled from "styled-components";
import {
  CommonAPI,
  CommonContentMessage,
  CommonMessage,
  CommonMomentEventWrapper,
  CommonMomentGameEventWrapper,
  CommonMomentSummary,
} from "../types";
import { formatTimestamp } from "../util";
import { IconButton, TooltipProvider } from "@bagel-web/components";
import {
  DisplaySource,
  EventBody,
  EventTableRow,
  GameEventRow,
  getMessageSearchText,
  MESSAGE_TABLE_HEADER_HEIGHT,
  MessageTableColumnVisibility,
} from "./message/Message";
import AgentName from "../agent/AgentName";
import { sessionQueries } from "../queries";
import { Badge } from "react-bootstrap";
import { AgencyTagFilter } from "../tag/AgencyTagFilter";
import { useAgentViewPreferences } from "../agent/viewPreferences/agentViewPreferences";

const ShowAllOverrideLink = styled.a`
  margin-left: 4px;
`;

// Carries the moment-N/moment-summary markers instead of the sticky cell:
// MomentNavigator locates the current moment from getBoundingClientRect,
// and the row keeps its natural layout position while the pinned cell of
// every passed moment reports the same stuck coordinates. scroll-margin
// keeps scrollIntoView targets clear of the sticky column headers.
const MomentHeaderRow = styled.tr`
  scroll-margin-top: ${MESSAGE_TABLE_HEADER_HEIGHT};
`;

// Pins directly below the sticky column headers. Every moment header shares
// the same "top", so each one paints over the previous one as it arrives
// (later rows win at equal z-index). Must stay below the column headers'
// z-index and be opaque so rows scrolling past aren't visible through it.
const MomentHeaderCell = styled.td<{ $pinned: boolean }>`
  position: sticky;
  top: calc(
    ${MESSAGE_TABLE_HEADER_HEIGHT} - var(--aa-page-body-padding-top, 0px)
  );
  z-index: 1;
  background-color: var(--bs-body-bg);
  /* Bottom border (as box-shadow, which travels with the sticky cell where a
     collapsed border would not) to make the cutoff clear when rows scroll
     underneath. Only shown while pinned. */
  box-shadow: ${({ $pinned }) =>
    $pinned ? "inset 0 -1px 0 var(--aa-border-subtle)" : "none"};
  padding-top: 10px;
  padding-bottom: 4px;
`;

// There is no CSS selector for "currently stuck", but it is detectable from
// geometry: sticky displaces the cell visually while its row keeps its natural
// layout position, so the cell is pinned exactly when it sits below its row.
const useIsPinned = (cellRef: RefObject<HTMLTableCellElement | null>) => {
  const [isPinned, setIsPinned] = useState(false);

  useEffect(() => {
    let rafId: number | null = null;
    const check = () => {
      rafId = null;
      const cell = cellRef.current;
      const row = cell?.parentElement;
      if (!cell || !row) return;
      setIsPinned(
        cell.getBoundingClientRect().top - row.getBoundingClientRect().top > 1
      );
    };
    const handleScroll = () => {
      if (rafId === null) rafId = requestAnimationFrame(check);
    };
    check();
    // Scroll events don't bubble, but they are observable from window in the
    // capture phase, so this hears the Page.Body scroll container without
    // needing a reference to it.
    window.addEventListener("scroll", handleScroll, true);
    return () => {
      window.removeEventListener("scroll", handleScroll, true);
      if (rafId !== null) cancelAnimationFrame(rafId);
    };
  }, [cellRef]);

  return isPinned;
};

const MomentLink = styled.a`
  font-weight: bold;
  width: fit-content;
  cursor: pointer;
`;

const MomentTimestamp = styled.span`
  font-style: italic;
  color: var(--aa-text-muted);
  margin: 0px 10px;
`;

const AgentCreatedCell = styled.div`
  display: flex;
  gap: 5px;
`;

const NUM_COLUMNS = 7;

const MomentHeader = ({
  moment,
  sessionId,
  agentId,
}: {
  moment: CommonMomentSummary;
  sessionId: string;
  agentId: string;
}) => {
  return (
    <>
      <MomentLink
        href={`/sessions/${sessionId}/agent/${agentId}?momentId=${moment.id}`}
      >
        Moment {moment.id}
      </MomentLink>
      <MomentTimestamp>{formatTimestamp(moment.created)}</MomentTimestamp>
    </>
  );
};

const AgentCreated = ({
  sessionId,
  agentId,
  commonAPI,
  numColumns,
}: {
  sessionId: string;
  agentId: string;
  commonAPI: CommonAPI;
  numColumns: number;
}) => {
  const queryClient = useQueryClient();

  const handleFetchAgentSummary = useCallback(
    async (sessionId: string, agentId: string) => {
      const session = await queryClient.fetchQuery({
        queryKey: sessionQueries.get(sessionId),
        queryFn: () => commonAPI.fetchSession(sessionId),
      });
      return session.agents.find((agent) => agent.id === agentId) ?? null;
    },
    [sessionId, agentId, queryClient, commonAPI.fetchSession]
  );

  return (
    <tr>
      <td colSpan={numColumns}>
        <AgentCreatedCell>
          <i>Agent created:</i>
          <AgentName
            sessionId={sessionId}
            agentId={agentId}
            fetchAgentSummary={handleFetchAgentSummary}
            showLink={true}
          />
        </AgentCreatedCell>
      </td>
    </tr>
  );
};

export const LLMError = ({
  errorMessage,
  columnVisibility,
}: {
  errorMessage: string | null;
  columnVisibility: MessageTableColumnVisibility;
}) => {
  return (
    <EventTableRow $backgroundColor="var(--aa-surface-bg)">
      <td align="right" className="pe-2">
        <DisplaySource source="assistant" />
      </td>
      <td></td>
      {columnVisibility.showChannels && <td></td>}
      {columnVisibility.showAgencyTags && <td></td>}
      <td>
        <EventBody>Error</EventBody>
      </td>
      <td></td>
      <td>
        <code>{errorMessage}</code>
      </td>
    </EventTableRow>
  );
};

const DisplayMoment = ({
  sessionId,
  agentId,
  moment,
  highlightedFnCallId = null,
  onHighlightFnCallId,
  showMessageTypes = [],
  showFunctionResults = [],
  showChalkLogLinks = false,
  agencyTagFilter,
  commonAPI,
  columnVisibility,
}: {
  sessionId: string;
  agentId: string;
  moment: CommonMomentSummary;
  highlightedFnCallId?: string | null;
  onHighlightFnCallId?: (fnCallId: string | null) => void;
  showMessageTypes?: Array<string>;
  showFunctionResults?: Array<string>;
  showChalkLogLinks?: boolean;
  agencyTagFilter: AgencyTagFilter;
  commonAPI: CommonAPI;
  columnVisibility: MessageTableColumnVisibility;
}) => {
  const [showAllOverride, setShowAllOverride] = useState(false);

  const numColumns =
    NUM_COLUMNS -
    (columnVisibility.showChannels ? 0 : 1) -
    (columnVisibility.showAgencyTags ? 0 : 1);

  const { messageStreamTextFilter, showNewestMessagesFirst } =
    useAgentViewPreferences();

  const { displayedEvents, hiddenMessageCount } = useMemo<{
    displayedEvents: Array<CommonMomentEventWrapper>;
    hiddenMessageCount: number;
  }>(() => {
    // Only GameEvents are valid for direct display in the Message Stream.
    // Other Session Events are present for data retrieval, but should not
    // be included directly in the list of messages.
    let gameEventWrappers = moment.event_wrappers.filter(
      (eventWrapper) => eventWrapper.event_type === "game_event"
    );

    const applyTextFilter = (
      wrappers: Array<CommonMomentEventWrapper>
    ): Array<CommonMomentEventWrapper> => {
      if (!messageStreamTextFilter) return wrappers;
      return wrappers.filter((eventWrapper) =>
        eventWrapper.event
          ? getMessageSearchText(eventWrapper.event as CommonMessage).includes(
              messageStreamTextFilter.toLowerCase()
            )
          : false
      );
    };

    gameEventWrappers = showNewestMessagesFirst
      ? gameEventWrappers.toReversed()
      : gameEventWrappers;

    if (showAllOverride) {
      return {
        displayedEvents: gameEventWrappers,
        hiddenMessageCount: 0,
      };
    }

    // Apply user-specified filters based on message type.
    let filteredEventWrappers =
      showMessageTypes.length > 0
        ? gameEventWrappers.filter((eventWrapper) => {
            return (
              eventWrapper.event?.message_type &&
              showMessageTypes.includes(
                eventWrapper.event?.message_type as string
              )
            );
          })
        : gameEventWrappers;

    // Apply user-specified filters based on message type.
    filteredEventWrappers =
      showFunctionResults.length > 0
        ? filteredEventWrappers.filter((eventWrapper) => {
            if (eventWrapper.event?.message_type === "ContentMessage") {
              const message = eventWrapper.event as CommonContentMessage;
              return (
                showFunctionResults.includes(
                  message.function_call?.type as string
                ) ||
                (showFunctionResults.includes("none") &&
                  !message.function_call?.type)
              );
            } else {
              return showFunctionResults.includes("none");
            }
          })
        : filteredEventWrappers;

    if (agencyTagFilter.tags.length > 0) {
      filteredEventWrappers = filteredEventWrappers.filter((eventWrapper) => {
        return agencyTagFilter.matchAny(
          eventWrapper.event?.agency_tags as Array<string> | null | undefined
        );
      });
    }

    filteredEventWrappers = applyTextFilter(filteredEventWrappers);

    const hiddenMessageCount =
      gameEventWrappers.length - filteredEventWrappers.length;
    return { displayedEvents: filteredEventWrappers, hiddenMessageCount };
  }, [
    moment,
    showAllOverride,
    JSON.stringify(showMessageTypes),
    JSON.stringify(showFunctionResults),
    JSON.stringify(agencyTagFilter.tags),
    messageStreamTextFilter,
    showNewestMessagesFirst,
  ]);

  const hasAgentCreation = useMemo<boolean>(() => {
    return moment.event_wrappers.some(
      (eventWrapper) => eventWrapper.event_type === "created_agent"
    );
  }, [moment]);

  const [hasLLMError, llmErrorMessage] = useMemo<
    [boolean, string | null]
  >(() => {
    const llmResultError = moment.event_wrappers.find(
      (eventWrapper) =>
        eventWrapper.event_type === "llm_result" &&
        eventWrapper.event?.error_message
    );
    return llmResultError
      ? [true, llmResultError?.event?.error_message as string | null]
      : [false, null];
  }, [moment]);

  const router = useRouter();
  const onViewChalkLog = () => {
    router.push(`/chalk-logs/${sessionId}/${moment.id}`);
  };

  const momentHeaderCellRef = useRef<HTMLTableCellElement>(null);
  const isPinned = useIsPinned(momentHeaderCellRef);

  return (
    <>
      <MomentHeaderRow
        id={`moment-${moment.id}`}
        data-moment-id={moment.id}
        className="moment-summary"
      >
        <MomentHeaderCell
          ref={momentHeaderCellRef}
          $pinned={isPinned}
          colSpan={numColumns}
        >
          <MomentHeader
            moment={moment}
            sessionId={sessionId}
            agentId={agentId}
          />
          {hasLLMError && <Badge bg="danger">Error</Badge>}
          {showChalkLogLinks && (
            <TooltipProvider tooltip="View Chalk Log">
              <IconButton
                className="bi-receipt-cutoff ms-2"
                onClick={() => onViewChalkLog()}
              />
            </TooltipProvider>
          )}
          <div style={{ float: "right" }}>
            {(hiddenMessageCount > 0 || showAllOverride) && (
              <ShowAllOverrideLink
                href="#"
                onClick={() => setShowAllOverride(!showAllOverride)}
              >
                {showAllOverride
                  ? "Hide filtered messages"
                  : `Show ${hiddenMessageCount} additional ${hiddenMessageCount == 1 ? "message" : "messages"}`}
                <i
                  style={{ marginLeft: "5px" }}
                  className={
                    showAllOverride ? "bi-chevron-up" : "bi-chevron-down"
                  }
                />
              </ShowAllOverrideLink>
            )}
          </div>
        </MomentHeaderCell>
      </MomentHeaderRow>
      {hasAgentCreation && (
        <AgentCreated
          sessionId={sessionId}
          agentId={agentId}
          commonAPI={commonAPI}
          numColumns={numColumns}
        />
      )}
      {!hasAgentCreation && displayedEvents.length === 0 && (
        <tr>
          <td colSpan={numColumns}>
            <i>No messages to display</i>
          </td>
        </tr>
      )}
      {displayedEvents.map((event) => {
        return (
          <GameEventRow
            key={event.id}
            gameEventWrapper={event as CommonMomentGameEventWrapper}
            moment={moment}
            highlightedFnCallId={highlightedFnCallId}
            onHighlightFnCallId={onHighlightFnCallId}
            textFilter={messageStreamTextFilter}
            columnVisibility={columnVisibility}
          />
        );
      })}
      {hasLLMError && (
        <LLMError
          errorMessage={llmErrorMessage}
          columnVisibility={columnVisibility}
        />
      )}
    </>
  );
};

export default DisplayMoment;
